import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import CargoClaimsUi from './CargoClaims.ui';
import { useCargoClaimsQuery } from './queries';
import toCargoClaim from './toCargoClaim';

import { CargoClaimsFilter, CargoClaimsParams } from './CargoClaims.types';

export default function CargoClaims() {
  //* Hooks Init
  const [searchParams, setSearchParams] = useSearchParams();

  const params: CargoClaimsParams = useMemo(
    () => ({
      page: searchParams.get('page') || '1',
      first: searchParams.get('first') || '0',
      rows: searchParams.get('rows') || '50',
      shipmentNumber: searchParams.get('shipmentNumber') || '',
      hawbNumber: searchParams.get('hawbNumber') || '',
      status: searchParams.get('status') || 'All',
      brand: searchParams.get('brand') || '',
    }),
    [searchParams]
  );

  //* Local State
  const [filter, setFilter] = useState<CargoClaimsFilter>({
    page: parseInt(params.page),
    first: parseInt(params.first),
    rows: parseInt(params.rows),
    shipmentNumber: params.shipmentNumber,
    hawbNumber: params.hawbNumber,
    status: params.status,
    brand: params.brand,
    dates: null,
  });

  /*
   * Filtering and paging happen in the service, not here.
   *
   * The URL params are what it is asked for, so a link to a filtered page
   * fetches that page rather than fetching everything and narrowing it in the
   * browser. That also means the row count is the service's, which is the only
   * one that can be right once a page is a page.
   */
  const claimsQuery = useCargoClaimsQuery(params);

  const claims = useMemo(
    () => (claimsQuery.data?.data.data ?? []).map(toCargoClaim),
    [claimsQuery.data]
  );

  const totalRecords = claimsQuery.data?.data.total ?? 0;

  //* Handlers
  function handleSearchChange(name: string, value: string) {
    setFilter((current) => ({ ...current, [name]: value }));
  }

  function handleSubmitSearch() {
    setSearchParams({
      page: '1',
      first: '0',
      rows: String(filter.rows),
      shipmentNumber: filter.shipmentNumber.trim(),
      hawbNumber: filter.hawbNumber.trim(),
      status: filter.status,
      brand: filter.brand.trim(),
    });
  }

  function handleDropdownChange(name: string, value: string) {
    setFilter((current) => ({ ...current, [name]: value }));

    setSearchParams({
      page: '1',
      first: '0',
      rows: String(filter.rows),
      shipmentNumber: filter.shipmentNumber.trim(),
      hawbNumber: filter.hawbNumber.trim(),
      status: name === 'status' ? value : filter.status,
      brand: name === 'brand' ? value : filter.brand.trim(),
    });
  }

  function handlePageChange(event: {
    page: number;
    first: number;
    rows: number;
  }) {
    setFilter((current) => ({
      ...current,
      page: event.page + 1,
      first: event.first,
      rows: event.rows,
    }));

    setSearchParams({
      page: String(event.page + 1),
      first: String(event.first),
      rows: String(event.rows),
      shipmentNumber: params.shipmentNumber,
      hawbNumber: params.hawbNumber,
      status: params.status,
      brand: params.brand,
    });
  }

  return (
    <CargoClaimsUi
      claims={claims}
      totalRecords={totalRecords}
      loading={claimsQuery.isLoading}
      filter={filter}
      handlers={{
        handleSearchChange,
        handleSubmitSearch,
        handleDropdownChange,
        handlePageChange,
      }}
    />
  );
}
